// src/pages/ConfiguracionNCF.jsx
// ═══════════════════════════════════════════════════════════════════════════════
// 🧾 CONFIGURACIÓN NCF - SISTEMA eFIT
// Gestión de secuencias NCF y configuración fiscal
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import api from "../services/api";
import Swal from "sweetalert2";

const ConfiguracionNCF = () => {
  // Estados
  const [secuencias, setSecuencias] = useState([]);
  const [tiposNcf, setTiposNcf] = useState([]);
  const [disponibilidad, setDisponibilidad] = useState([]);
  const [configFiscal, setConfigFiscal] = useState({
    rnc: "",
    razon_social: "",
    nombre_comercial: "",
    direccion: "",
    telefono: "",
    ambiente: "produccion",
    // Defaults de la migración 035. Importan para los tenants que todavía no
    // tienen fila en configuracion_fiscal: la carga solo reemplaza el estado si
    // la fila trae rnc, así que para ellos este objeto es lo que ve el formulario.
    retencion_isr_pf: 15.00,
    retencion_itbis_pf: 100.00
  });
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [modalSecuencia, setModalSecuencia] = useState(false);
  const [secuenciaEditando, setSecuenciaEditando] = useState(null);

  // Cargar datos iniciales
  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [secRes, tiposRes, dispRes, configRes] = await Promise.all([
        api.get("/ncf/secuencias"),
        api.get("/ncf/tipos"),
        api.get("/ncf/disponibilidad"),
        api.get("/ncf/configuracion-fiscal")
      ]);
      setSecuencias(secRes.data);
      setTiposNcf(tiposRes.data);
      setDisponibilidad(dispRes.data);
      if (configRes.data && configRes.data.rnc) {
        setConfigFiscal(configRes.data);
      }
    } catch (error) {
      console.error("Error al cargar datos:", error);
    } finally {
      setLoading(false);
    }
  };

  // Guardar configuración fiscal
  const guardarConfigFiscal = async () => {
    if (!configFiscal.rnc || !configFiscal.razon_social) {
      Swal.fire({
        icon: "warning",
        title: "Campos requeridos",
        text: "RNC y Razón Social son obligatorios"
      });
      return;
    }

    // Tasas de retención PF. El backend ya responde 400 ante un valor inválido
    // (ncfController.guardarConfiguracionFiscal), pero aquí el aviso es legible
    // y sigue el patrón del chequeo de RNC / Razón Social de arriba.
    const tasasPf = [
      ["retencion_isr_pf",   "Retención ISR Persona Física"],
      ["retencion_itbis_pf", "Retención ITBIS Persona Física"],
    ];
    for (const [campo, etiqueta] of tasasPf) {
      const valor  = configFiscal[campo];
      const numero = Number(valor);
      if (valor === "" || valor === null || valor === undefined ||
          !Number.isFinite(numero) || numero < 0 || numero > 100) {
        Swal.fire({
          icon: "warning",
          title: "Tasa inválida",
          text: `${etiqueta} debe ser un número entre 0 y 100`
        });
        return;
      }
    }

    try {
      setGuardando(true);
      await api.post("/ncf/configuracion-fiscal", configFiscal);
      Swal.fire({
        icon: "success",
        title: "Guardado",
        text: "Configuración fiscal guardada correctamente",
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo guardar la configuración"
      });
    } finally {
      setGuardando(false);
    }
  };

  // Determina la serie por el código — no depende del campo serie del API
  const getSerieParaTipo = (codigo) => {
    return ['31','32','33','34'].includes(codigo) ? 'E' : 'B';
  };

  // Abrir modal para nueva secuencia
  const nuevaSecuencia = () => {
    setSecuenciaEditando({
      tipo_ncf: "02",
      serie: "B",
      secuencia_inicial: 1,
      secuencia_final: 500,
      fecha_vencimiento: "",
      activo: true
    });
    setModalSecuencia(true);
  };

  // Editar secuencia existente
  const editarSecuencia = (secuencia) => {
    setSecuenciaEditando({
      ...secuencia,
      fecha_vencimiento: secuencia.fecha_vencimiento?.split("T")[0] || ""
    });
    setModalSecuencia(true);
  };

  // Guardar secuencia
  const guardarSecuencia = async () => {
    if (!secuenciaEditando.tipo_ncf || !secuenciaEditando.secuencia_final || !secuenciaEditando.fecha_vencimiento) {
      Swal.fire({
        icon: "warning",
        title: "Campos requeridos",
        text: "Complete todos los campos obligatorios"
      });
      return;
    }

    try {
      setGuardando(true);
      await api.post("/ncf/secuencias", secuenciaEditando);
      Swal.fire({
        icon: "success",
        title: "Guardado",
        text: "Secuencia NCF guardada correctamente",
        timer: 2000,
        showConfirmButton: false
      });
      setModalSecuencia(false);
      setSecuenciaEditando(null);
      cargarDatos();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo guardar la secuencia"
      });
    } finally {
      setGuardando(false);
    }
  };

  // Obtener nombre del tipo NCF
  const getNombreTipo = (codigo) => {
    const tipo = tiposNcf.find(t => t.codigo === codigo);
    return tipo ? tipo.nombre : codigo;
  };

  // Formatear fecha
  const formatearFecha = (fecha) => {
    if (!fecha) return "-";
    const [y, m, d] = String(fecha).substring(0, 10).split('-'); return `${d}/${m}/${y}`;
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Cargando configuración NCF...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>🧾 Configuración NCF</h1>
          <p style={styles.subtitle}>Gestión de comprobantes fiscales</p>
        </div>
      </div>

      {/* Alertas de disponibilidad */}
      {disponibilidad.some(d => d.alerta) && (
        <div style={styles.alertCard}>
          <div style={styles.alertIcon}>⚠️</div>
          <div style={styles.alertContent}>
            <h4 style={styles.alertTitle}>Alertas de NCF</h4>
            {disponibilidad.filter(d => d.alerta).map((d, idx) => (
              <p key={idx} style={styles.alertText}>
                {d.vencido 
                  ? `❌ ${getNombreTipo(d.tipo_ncf)} VENCIDO (${formatearFecha(d.fecha_vencimiento)})`
                  : `⚠️ ${getNombreTipo(d.tipo_ncf)} - Solo quedan ${d.disponibles} comprobantes`
                }
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Tarjetas de disponibilidad */}
      <div style={styles.disponibilidadGrid}>
        {disponibilidad.map((d, idx) => (
          <div 
            key={idx} 
            style={{
              ...styles.disponibilidadCard,
              borderColor: d.vencido ? "#ef4444" : d.alerta ? "#f59e0b" : "#10b981"
            }}
          >
            <div style={styles.dispHeader}>
              <span style={styles.dispTipo}>{d.serie}{d.tipo_ncf}</span>
              <span style={{
                ...styles.dispEstado,
                background: d.vencido ? "#fee2e2" : d.alerta ? "#fef3c7" : "#d1fae5",
                color: d.vencido ? "#991b1b" : d.alerta ? "#92400e" : "#065f46"
              }}>
                {d.vencido ? "Vencido" : d.alerta ? "Alerta" : "Activo"}
              </span>
            </div>
            <h3 style={styles.dispNombre}>{getNombreTipo(d.tipo_ncf)}</h3>
            <div style={styles.dispStats}>
              <div style={styles.dispStat}>
                <span style={styles.dispStatLabel}>Disponibles</span>
                <span style={styles.dispStatValue}>{d.disponibles}</span>
              </div>
              <div style={styles.dispStat}>
                <span style={styles.dispStatLabel}>Usados</span>
                <span style={styles.dispStatValue}>{d.usados}</span>
              </div>
              <div style={styles.dispStat}>
                <span style={styles.dispStatLabel}>Total</span>
                <span style={styles.dispStatValue}>{d.total}</span>
              </div>
            </div>
            <div style={styles.dispProgress}>
              <div 
                style={{
                  ...styles.dispProgressBar,
                  width: `${d.porcentaje_usado}%`,
                  background: parseFloat(d.porcentaje_usado) > 80 ? "#ef4444" : "#3b82f6"
                }}
              ></div>
            </div>
            <p style={styles.dispVence}>Vence: {formatearFecha(d.fecha_vencimiento)}</p>
          </div>
        ))}
      </div>

      {/* Configuración Fiscal */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>🏢 Datos Fiscales de la Empresa</h2>
        <div style={styles.formGrid}>
          <div style={styles.formGroup}>
            <label style={styles.label}>RNC *</label>
            <input
              type="text"
              style={styles.input}
              value={configFiscal.rnc}
              onChange={(e) => setConfigFiscal({...configFiscal, rnc: e.target.value})}
              placeholder="000-00000-0"
              maxLength={15}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Razón Social *</label>
            <input
              type="text"
              style={styles.input}
              value={configFiscal.razon_social}
              onChange={(e) => setConfigFiscal({...configFiscal, razon_social: e.target.value})}
              placeholder="Nombre legal de la empresa"
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Nombre Comercial</label>
            <input
              type="text"
              style={styles.input}
              value={configFiscal.nombre_comercial || ""}
              onChange={(e) => setConfigFiscal({...configFiscal, nombre_comercial: e.target.value})}
              placeholder="Nombre comercial"
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Teléfono</label>
            <input
              type="text"
              style={styles.input}
              value={configFiscal.telefono || ""}
              onChange={(e) => setConfigFiscal({...configFiscal, telefono: e.target.value})}
              placeholder="809-000-0000"
            />
          </div>
          <div style={{...styles.formGroup, gridColumn: "1 / -1"}}>
            <label style={styles.label}>Dirección</label>
            <input
              type="text"
              style={styles.input}
              value={configFiscal.direccion || ""}
              onChange={(e) => setConfigFiscal({...configFiscal, direccion: e.target.value})}
              placeholder="Dirección completa"
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Ambiente</label>
            <select
              style={styles.select}
              value={configFiscal.ambiente}
              onChange={(e) => setConfigFiscal({...configFiscal, ambiente: e.target.value})}
            >
              <option value="produccion">Producción</option>
              <option value="pruebas">Pruebas</option>
            </select>
          </div>
        </div>
        <div style={styles.formActions}>
          <button 
            style={styles.btnPrimary}
            onClick={guardarConfigFiscal}
            disabled={guardando}
          >
            {guardando ? "Guardando..." : "💾 Guardar Configuración Fiscal"}
          </button>
        </div>
      </div>

      {/* Secuencias NCF */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>📋 Secuencias NCF Autorizadas</h2>
          <button style={styles.btnSuccess} onClick={nuevaSecuencia}>
            ➕ Agregar Secuencia
          </button>
        </div>
        
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Tipo</th>
                <th style={styles.th}>Serie</th>
                <th style={styles.th}>Rango</th>
                <th style={styles.th}>Actual</th>
                <th style={styles.th}>Disponibles</th>
                <th style={styles.th}>Vencimiento</th>
                <th style={styles.th}>Estado</th>
                <th style={styles.th}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {secuencias.length === 0 ? (
                <tr>
                  <td colSpan="8" style={styles.emptyRow}>
                    No hay secuencias NCF registradas
                  </td>
                </tr>
              ) : (
                secuencias.map((s) => {
                  const disponibles = s.secuencia_final - s.secuencia_actual;
                  const vencido = new Date() > new Date(s.fecha_vencimiento);
                  return (
                    <tr key={s.id} style={styles.tr}>
                      <td style={styles.td}>
                        <strong>{s.serie}{s.tipo_ncf}</strong>
                        <br />
                        <small style={{color: "#64748b"}}>{getNombreTipo(s.tipo_ncf)}</small>
                      </td>
                      <td style={styles.td}>{s.serie}</td>
                      <td style={styles.td}>{s.secuencia_inicial} - {s.secuencia_final}</td>
                      <td style={styles.td}><strong>{s.secuencia_actual}</strong></td>
                      <td style={styles.td}>
                        <span style={{
                          ...styles.badge,
                          background: disponibles < 50 ? "#fee2e2" : "#d1fae5",
                          color: disponibles < 50 ? "#991b1b" : "#065f46"
                        }}>
                          {disponibles}
                        </span>
                      </td>
                      <td style={styles.td}>{formatearFecha(s.fecha_vencimiento)}</td>
                      <td style={styles.td}>
                        <span style={{
                          ...styles.badge,
                          background: vencido ? "#fee2e2" : s.activo ? "#d1fae5" : "#f1f5f9",
                          color: vencido ? "#991b1b" : s.activo ? "#065f46" : "#64748b"
                        }}>
                          {vencido ? "Vencido" : s.activo ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <button 
                          style={styles.btnEdit}
                          onClick={() => editarSecuencia(s)}
                        >
                          ✏️ Editar
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Información de tipos NCF */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>📖 Tipos de Comprobantes Fiscales</h2>
        {["B", "E"].map((serie) => {
          const tiposSerie = tiposNcf.filter(t => getSerieParaTipo(t.codigo) === serie);
          if (tiposSerie.length === 0) return null;
          return (
            <div key={serie} style={{marginBottom: "20px"}}>
              <p style={{
                fontSize: "12px", fontWeight: "700", color: serie === "E" ? "#0f766e" : "#1e3a8a",
                textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "10px"
              }}>
                {serie === "E" ? "⚡ Serie E — Electrónicos (e-CF)" : "📋 Serie B — Tradicionales"}
              </p>
              <div style={styles.tiposGrid}>
                {tiposSerie.map((tipo, idx) => (
                  <div key={idx} style={styles.tipoCard}>
                    <div style={{
                      ...styles.tipoCodigo,
                      background: serie === "E"
                        ? "linear-gradient(135deg, #0f766e 0%, #0d9488 100%)"
                        : "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)"
                    }}>
                      {getSerieParaTipo(tipo.codigo)}{tipo.codigo}
                    </div>
                    <div>
                      <h4 style={styles.tipoNombre}>{tipo.nombre}</h4>
                      <p style={styles.tipoDesc}>{tipo.descripcion}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal de Secuencia */}
      {modalSecuencia && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <h3 style={styles.modalTitle}>
              {secuenciaEditando?.id ? "✏️ Editar Secuencia" : "➕ Nueva Secuencia NCF"}
            </h3>
            
            <div style={styles.formGrid}>
              <div style={{...styles.formGroup, gridColumn: "1 / -1"}}>
                <label style={styles.label}>Tipo de NCF *</label>
                <select
                  style={styles.select}
                  value={secuenciaEditando?.tipo_ncf || ""}
                  onChange={(e) => {
                    const codigo = e.target.value;
                    const serie = getSerieParaTipo(codigo);
                    setSecuenciaEditando({...secuenciaEditando, tipo_ncf: codigo, serie});
                  }}
                >
                  <option value="">Seleccionar...</option>
                  <option disabled value="">── Serie B — Tradicionales ──</option>
                  {tiposNcf.filter(t => getSerieParaTipo(t.codigo) === "B").map((tipo) => (
                    <option key={tipo.codigo} value={tipo.codigo}>
                      B{tipo.codigo} - {tipo.nombre}
                    </option>
                  ))}
                  <option disabled value="">── Serie E — Electrónicos (e-CF) ──</option>
                  {tiposNcf.filter(t => getSerieParaTipo(t.codigo) === "E").map((tipo) => (
                    <option key={tipo.codigo} value={tipo.codigo}>
                      E{tipo.codigo} - {tipo.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Serie</label>
                <input
                  type="text"
                  style={{...styles.input, background: "#f8fafc", color: "#475569"}}
                  value={secuenciaEditando?.serie || "B"}
                  readOnly
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Secuencia Inicial *</label>
                <input
                  type="number"
                  style={styles.input}
                  value={secuenciaEditando?.secuencia_inicial || ""}
                  onChange={(e) => setSecuenciaEditando({...secuenciaEditando, secuencia_inicial: parseInt(e.target.value) || 0})}
                  min={1}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Secuencia Final *</label>
                <input
                  type="number"
                  style={styles.input}
                  value={secuenciaEditando?.secuencia_final || ""}
                  onChange={(e) => setSecuenciaEditando({...secuenciaEditando, secuencia_final: parseInt(e.target.value) || 0})}
                  min={1}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Fecha de Vencimiento *</label>
                <input
                  type="date"
                  style={styles.input}
                  value={secuenciaEditando?.fecha_vencimiento || ""}
                  onChange={(e) => setSecuenciaEditando({...secuenciaEditando, fecha_vencimiento: e.target.value})}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Estado</label>
                <select
                  style={styles.select}
                  value={secuenciaEditando?.activo ? "true" : "false"}
                  onChange={(e) => setSecuenciaEditando({...secuenciaEditando, activo: e.target.value === "true"})}
                >
                  <option value="true">Activo</option>
                  <option value="false">Inactivo</option>
                </select>
              </div>
            </div>

            <div style={styles.modalActions}>
              <button 
                style={styles.btnCancel}
                onClick={() => {
                  setModalSecuencia(false);
                  setSecuenciaEditando(null);
                }}
              >
                Cancelar
              </button>
              <button 
                style={styles.btnSuccess}
                onClick={guardarSecuencia}
                disabled={guardando}
              >
                {guardando ? "Guardando..." : "💾 Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Animaciones */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 🎨 ESTILOS
// ═══════════════════════════════════════════════════════════════════════════════
const styles = {
  container: {
    minHeight: "100vh",
    background: "linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)",
    padding: "24px 32px",
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  loadingContainer: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
    color: "#64748b",
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "4px solid #e2e8f0",
    borderTopColor: "#3b82f6",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  header: {
    marginBottom: "24px",
  },
  title: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#1e293b",
    margin: 0,
  },
  subtitle: {
    fontSize: "14px",
    color: "#64748b",
    margin: "4px 0 0 0",
  },

  // Alerta
  alertCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: "16px",
    padding: "16px 20px",
    background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
    border: "2px solid #f59e0b",
    borderRadius: "12px",
    marginBottom: "24px",
  },
  alertIcon: {
    fontSize: "28px",
  },
  alertContent: {
    flex: 1,
  },
  alertTitle: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#92400e",
    margin: "0 0 8px 0",
  },
  alertText: {
    fontSize: "14px",
    color: "#92400e",
    margin: "4px 0",
  },

  // Disponibilidad Grid
  disponibilidadGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
    gap: "16px",
    marginBottom: "24px",
  },
  disponibilidadCard: {
    background: "white",
    borderRadius: "16px",
    padding: "20px",
    border: "2px solid",
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
  },
  dispHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
  },
  dispTipo: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#3b82f6",
  },
  dispEstado: {
    padding: "4px 10px",
    borderRadius: "20px",
    fontSize: "11px",
    fontWeight: "700",
  },
  dispNombre: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#1e293b",
    margin: "0 0 12px 0",
  },
  dispStats: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "12px",
  },
  dispStat: {
    textAlign: "center",
  },
  dispStatLabel: {
    display: "block",
    fontSize: "10px",
    color: "#64748b",
    marginBottom: "2px",
  },
  dispStatValue: {
    display: "block",
    fontSize: "18px",
    fontWeight: "700",
    color: "#1e293b",
  },
  dispProgress: {
    height: "6px",
    background: "#e2e8f0",
    borderRadius: "3px",
    overflow: "hidden",
    marginBottom: "8px",
  },
  dispProgressBar: {
    height: "100%",
    borderRadius: "3px",
    transition: "width 0.3s ease",
  },
  dispVence: {
    fontSize: "11px",
    color: "#64748b",
    margin: 0,
    textAlign: "center",
  },

  // Card
  card: {
    background: "white",
    borderRadius: "16px",
    padding: "24px",
    marginBottom: "24px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
    border: "1px solid #e2e8f0",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  cardTitle: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#1e293b",
    margin: 0,
  },

  // Form
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "16px",
    marginBottom: "20px",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
  },
  label: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#475569",
    marginBottom: "6px",
  },
  input: {
    padding: "12px",
    fontSize: "14px",
    border: "2px solid #e2e8f0",
    borderRadius: "8px",
    outline: "none",
    transition: "border-color 0.2s",
  },
  select: {
    padding: "12px",
    fontSize: "14px",
    border: "2px solid #e2e8f0",
    borderRadius: "8px",
    outline: "none",
    background: "white",
    cursor: "pointer",
  },
  formActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
  },

  // Buttons
  btnPrimary: {
    padding: "12px 24px",
    background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },
  btnSuccess: {
    padding: "10px 20px",
    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },
  btnCancel: {
    padding: "10px 20px",
    background: "#94a3b8",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },
  btnEdit: {
    padding: "8px 16px",
    background: "#f59e0b",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
  },

  // Table
  tableWrapper: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    padding: "12px",
    textAlign: "left",
    fontSize: "12px",
    fontWeight: "700",
    color: "#64748b",
    borderBottom: "2px solid #e2e8f0",
    background: "#f8fafc",
  },
  tr: {
    borderBottom: "1px solid #f1f5f9",
  },
  td: {
    padding: "12px",
    fontSize: "13px",
    color: "#1e293b",
  },
  emptyRow: {
    padding: "40px",
    textAlign: "center",
    color: "#94a3b8",
    fontStyle: "italic",
  },
  badge: {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "600",
  },

  // Tipos Grid
  tiposGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: "12px",
  },
  tipoCard: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "16px",
    background: "#f8fafc",
    borderRadius: "10px",
    border: "1px solid #e2e8f0",
  },
  tipoCodigo: {
    padding: "12px 16px",
    background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
    color: "white",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "700",
  },
  tipoNombre: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#1e293b",
    margin: "0 0 4px 0",
  },
  tipoDesc: {
    fontSize: "12px",
    color: "#64748b",
    margin: 0,
  },

  // Modal
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modalContent: {
    background: "white",
    borderRadius: "16px",
    padding: "24px",
    width: "90%",
    maxWidth: "600px",
    maxHeight: "90vh",
    overflowY: "auto",
  },
  modalTitle: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#1e293b",
    marginBottom: "20px",
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    marginTop: "20px",
  },
};

export default ConfiguracionNCF;
